import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, Send, CheckCircle, XCircle, Clock, Download, 
  Plus, RefreshCw, Building2, ChevronRight, AlertCircle,
  TrendingUp, Calendar, Filter, Search, Eye, MoreVertical
} from 'lucide-react';

const API = `${window.location.origin}/api`;

const EInvoiceDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [newProperty, setNewProperty] = useState({
    property_name: '',
    business_name: '',
    lhdn_tin: '',
    sst_number: '',
    address: '',
    city: '',
    state: 'Kuala Lumpur',
    postal_code: '',
    email: '',
    phone: '',
    tax_rate: 0
  });

  const malaysiaStates = [
    'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 
    'Pahang', 'Pulau Pinang', 'Perak', 'Perlis', 'Selangor', 
    'Terengganu', 'Sabah', 'Sarawak', 'Kuala Lumpur', 'Labuan', 'Putrajaya'
  ];

  useEffect(() => {
    fetchData();
  }, [selectedProperty]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const propertyParam = selectedProperty !== 'all' ? `?property_id=${selectedProperty}` : '';
      
      const [statsRes, invoicesRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/einvoice/dashboard${propertyParam}`),
        axios.get(`${API}/einvoice/invoices${propertyParam}&page_size=10`),
        axios.get(`${API}/einvoice/properties`)
      ]);
      
      setStats(statsRes.data);
      setRecentInvoices(invoicesRes.data.invoices || []);
      setProperties(propertiesRes.data.properties || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProperty = async () => {
    if (!newProperty.property_name || !newProperty.lhdn_tin || !newProperty.address) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.post(`${API}/einvoice/properties?user_id=${user.id}`, newProperty);
      toast.success('Property created successfully');
      setShowPropertyModal(false);
      setNewProperty({
        property_name: '', business_name: '', lhdn_tin: '', sst_number: '',
        address: '', city: '', state: 'Kuala Lumpur', postal_code: '',
        email: '', phone: '', tax_rate: 0
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create property');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'secondary', icon: Clock, color: 'text-yellow-600' },
      submitted: { variant: 'default', icon: Send, color: 'text-blue-600' },
      validated: { variant: 'success', icon: CheckCircle, color: 'text-green-600' },
      rejected: { variant: 'destructive', icon: XCircle, color: 'text-red-600' },
      cancelled: { variant: 'outline', icon: AlertCircle, color: 'text-gray-500' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {status?.toUpperCase()}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-MY', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="einvoice-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">e-Invoice Dashboard</h1>
          <p className="text-slate-600">Malaysia LHDN Compliant e-Invoice Management</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedProperty} onValueChange={setSelectedProperty}>
            <SelectTrigger className="w-[200px]" data-testid="property-filter">
              <SelectValue placeholder="All Properties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Properties</SelectItem>
              {properties.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.property_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setShowPropertyModal(true)} data-testid="add-property-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Property
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card data-testid="stat-invoices-today">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Today</p>
                <p className="text-2xl font-bold">{stats?.invoices_today || 0}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-invoices-month">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">This Month</p>
                <p className="text-2xl font-bold">{stats?.invoices_this_month || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-pending">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats?.pending_submission || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-validated">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Validated</p>
                <p className="text-2xl font-bold text-green-600">{stats?.validated || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-rejected">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Rejected</p>
                <p className="text-2xl font-bold text-red-600">{stats?.rejected || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-revenue">
          <CardContent className="pt-4">
            <div>
              <p className="text-xs text-slate-500 uppercase">Revenue (Month)</p>
              <p className="text-lg font-bold text-blue-600">
                {formatCurrency(stats?.revenue_invoiced_month)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Invoices */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Recent e-Invoices</CardTitle>
            <CardDescription>Latest invoices and their LHDN status</CardDescription>
          </div>
          <Button variant="outline" onClick={() => window.location.href = '/einvoice/invoices'}>
            View All <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {recentInvoices.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No invoices yet. Import bookings or create invoices to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Guest</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>LHDN Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentInvoices.map((invoice) => (
                  <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                    <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                    <TableCell>{invoice.guest_name || '-'}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(invoice.total)}
                    </TableCell>
                    <TableCell>{getStatusBadge(invoice.lhdn_status)}</TableCell>
                    <TableCell className="text-slate-500">{formatDate(invoice.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" title="View">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" title="Download PDF">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Properties Overview */}
      {properties.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Registered Properties</CardTitle>
            <CardDescription>Properties configured for e-Invoice</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {properties.map((property) => (
                <div 
                  key={property.id} 
                  className="p-4 border rounded-lg hover:border-blue-300 transition-colors"
                  data-testid={`property-card-${property.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold">{property.property_name}</h4>
                      <p className="text-sm text-slate-500">{property.business_name || property.property_name}</p>
                    </div>
                    <Building2 className="w-5 h-5 text-slate-400" />
                  </div>
                  <div className="mt-3 space-y-1 text-sm">
                    <p><span className="text-slate-500">TIN:</span> {property.lhdn_tin}</p>
                    <p><span className="text-slate-500">SST:</span> {property.sst_number || 'NA'}</p>
                    <p><span className="text-slate-500">Location:</span> {property.city}, {property.state}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Property Modal */}
      <Dialog open={showPropertyModal} onOpenChange={setShowPropertyModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Property</DialogTitle>
            <DialogDescription>
              Register a property for LHDN e-Invoice compliance
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="property_name">Property Name *</Label>
                <Input
                  id="property_name"
                  value={newProperty.property_name}
                  onChange={(e) => setNewProperty({...newProperty, property_name: e.target.value})}
                  placeholder="Hotel ABC"
                  data-testid="input-property-name"
                />
              </div>
              <div>
                <Label htmlFor="business_name">Legal Business Name</Label>
                <Input
                  id="business_name"
                  value={newProperty.business_name}
                  onChange={(e) => setNewProperty({...newProperty, business_name: e.target.value})}
                  placeholder="ABC Hospitality Sdn Bhd"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lhdn_tin">TIN (Tax ID Number) *</Label>
                <Input
                  id="lhdn_tin"
                  value={newProperty.lhdn_tin}
                  onChange={(e) => setNewProperty({...newProperty, lhdn_tin: e.target.value})}
                  placeholder="C12345678901"
                  data-testid="input-lhdn-tin"
                />
              </div>
              <div>
                <Label htmlFor="sst_number">SST Registration Number</Label>
                <Input
                  id="sst_number"
                  value={newProperty.sst_number}
                  onChange={(e) => setNewProperty({...newProperty, sst_number: e.target.value})}
                  placeholder="B12-1234-56789012"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                value={newProperty.address}
                onChange={(e) => setNewProperty({...newProperty, address: e.target.value})}
                placeholder="123 Jalan Example"
                data-testid="input-address"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={newProperty.city}
                  onChange={(e) => setNewProperty({...newProperty, city: e.target.value})}
                  placeholder="Kuala Lumpur"
                />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Select 
                  value={newProperty.state} 
                  onValueChange={(v) => setNewProperty({...newProperty, state: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {malaysiaStates.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="postal_code">Postal Code</Label>
                <Input
                  id="postal_code"
                  value={newProperty.postal_code}
                  onChange={(e) => setNewProperty({...newProperty, postal_code: e.target.value})}
                  placeholder="50000"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newProperty.email}
                  onChange={(e) => setNewProperty({...newProperty, email: e.target.value})}
                  placeholder="hotel@example.com"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={newProperty.phone}
                  onChange={(e) => setNewProperty({...newProperty, phone: e.target.value})}
                  placeholder="+60123456789"
                />
              </div>
            </div>

            <div className="w-1/3">
              <Label htmlFor="tax_rate">SST Rate (%)</Label>
              <Input
                id="tax_rate"
                type="number"
                min="0"
                max="100"
                value={newProperty.tax_rate}
                onChange={(e) => setNewProperty({...newProperty, tax_rate: parseFloat(e.target.value) || 0})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPropertyModal(false)}>Cancel</Button>
            <Button onClick={handleCreateProperty} data-testid="save-property-btn">
              Create Property
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EInvoiceDashboard;
